import { Suspense, lazy, useEffect, useMemo, useRef, useState } from "react";
import { api } from "../api";
import { missingFields } from "../format";
import { Link } from "../router";
import type { Citation, DraftDetail, Slot } from "../types";
// Loaded when a document opens: the editor is most of the app's weight,
// and the chat should not wait for it.
const DocEditor = lazy(() => import("./DocEditor").then((m) => ({ default: m.DocEditor })));
import { DocumentPanel } from "./DocumentPanel";
import { FieldPicker, type Waiting } from "./FieldPicker";
import { IconBack, IconDownload, IconExpand } from "./Icons";

/**
 * One draft: the fields on the left, the document on the right.
 *
 * Two things here are the point of the feature, and neither is the form.
 *
 * The **gap list** (Lipsă · N) names every required field still empty and
 * the provision that requires it. A template site can list empty fields;
 * only something sitting on the corpus can say why each must be filled.
 *
 * The **preview** marks what the lawyer filled and what is still a gap,
 * from segments the server returns - so the boundary between a value and
 * the template's wording survives rendering, and a value that happens to
 * read like the sentence around it is still visibly a value.
 *
 * Fields commit on blur: every save re-renders the document server-side,
 * and doing that per keystroke would make typing feel like a remote shell.
 */

interface Section {
  name: string;
  slots: Slot[];
  missing: number;
}

/**
 * Every section of the document, each with its fields - including a section
 * with none yet, since an added field can be placed in any of them. A field
 * added in this draft and not yet placed is in none: it waits under "+".
 */
function sectionsOf(draft: DraftDetail): Section[] {
  const out: Section[] = (draft.sections ?? []).map((name) => ({ name, slots: [], missing: 0 }));
  const byName = new Map(out.map((s) => [s.name, s]));
  for (const slot of draft.slots) {
    if (slot.added && !slot.section) continue;
    const name = slot.section || "Date generale";
    let sec = byName.get(name);
    if (!sec) {
      sec = { name, slots: [], missing: 0 };
      byName.set(name, sec);
      out.push(sec);
    }
    sec.slots.push(slot);
    if (slot.required && !(draft.values[slot.key] ?? "").trim()) sec.missing += 1;
  }
  return out;
}

function waitingOf(draft: DraftDetail): Waiting[] {
  return draft.slots
    .filter((s) => s.added && !s.section)
    .map((s) => ({ key: s.key, label: s.label }));
}

function provisionCitation(docId: string, path: string, note: string): Citation {
  // The panel is driven by a Citation; a field has a path but no chunk.
  // `snippet` stays empty: it is the highlight fallback, and invented text
  // would highlight the wrong passage.
  return {
    chunk_id: `provision:${docId}:${path}`,
    doc_id: docId,
    family_id: "",
    title: "",
    label: note || "Temei legal",
    path,
    effective_from: null,
    effective_to: null,
    is_current: true,
    snippet: "",
  };
}

function FieldInput({
  slot,
  value,
  ring,
  onCommit,
  onOpenSource,
}: {
  slot: Slot;
  value: string;
  ring: boolean;
  onCommit: (value: string) => void;
  onOpenSource: (docId: string, path: string, note: string) => void;
}) {
  const [text, setText] = useState(value);
  useEffect(() => setText(value), [value]);

  const empty = !text.trim();
  const id = `f-${slot.key}`;
  const cls = ["field", empty && slot.required ? "empty" : "", ring ? "focus" : ""]
    .filter(Boolean)
    .join(" ");
  const src = slot.source;

  return (
    <div className={cls} id={`a-${slot.key}`}>
      <div className="field-label">
        <label htmlFor={id}>
          {slot.label}
          {slot.required && <span className="req" title="Obligatoriu"> *</span>}
        </label>
        {src &&
          (src.doc_id ? (
            <button
              className="art"
              onClick={() => onOpenSource(src.doc_id!, src.path, src.note)}
              title="Deschide prevederea"
            >
              {src.note}
            </button>
          ) : (
            <span className="art">{src.note}</span>
          ))}
      </div>
      {slot.kind === "longtext" ? (
        <textarea
          id={id}
          rows={3}
          value={text}
          placeholder={slot.help_text || undefined}
          onChange={(e) => setText(e.target.value)}
          onBlur={() => text !== value && onCommit(text)}
        />
      ) : (
        <input
          id={id}
          type="text"
          value={text}
          placeholder={slot.help_text || undefined}
          onChange={(e) => setText(e.target.value)}
          onBlur={() => text !== value && onCommit(text)}
        />
      )}
      {src && !src.present && (
        <span className="field-warn">Norma citată nu mai e găsită în versiunea în vigoare.</span>
      )}
      {src?.possibly_amended && (
        <span className="field-warn">Actul a fost modificat după redactarea modelului — verificați.</span>
      )}
    </div>
  );
}

const SAVE_LABEL = {
  "": "",
  dirty: "modificări nesalvate…",
  saving: "se salvează…",
  saved: "salvat",
  error: "nesalvat",
} as const;

/** "Exportă": the .docx to edit in Word, the .pdf to send. */
function ExportMenu({ id, large = false }: { id: string; large?: boolean }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as globalThis.Node)) setOpen(false);
    };
    document.addEventListener("mousedown", away);
    return () => document.removeEventListener("mousedown", away);
  }, [open]);

  async function get(format: "docx" | "pdf") {
    setOpen(false);
    setBusy(format);
    setError(null);
    try {
      await api.exportDraft(id, format);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Exportul a eșuat.");
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="export" ref={box}>
      <button
        className={large ? "btn medium primary" : "btn small primary"}
        aria-expanded={open}
        disabled={busy !== null}
        onClick={() => setOpen((o) => !o)}
      >
        <IconDownload size={16} /> {busy ? "Se pregătește…" : "Exportă"} ▾
      </button>
      {open && (
        <div className="export-menu" role="menu">
          <button role="menuitem" className="picker-item" onClick={() => void get("docx")}>
            <span>Word</span><code>.docx</code>
          </button>
          <button role="menuitem" className="picker-item" onClick={() => void get("pdf")}>
            <span>PDF</span><code>.pdf</code>
          </button>
        </div>
      )}
      {error && <span className="export-error" role="alert">{error}</span>}
    </div>
  );
}

export function DraftEditor({
  draftId,
  onBack,
  onOpenSource,
  variant = "page",
}: {
  draftId: string;
  onBack?: () => void;
  /** Panel variant: the chat owns the source panel. */
  onOpenSource?: (docId: string, path: string) => void;
  /**
   * "panel" is the same editor beside a conversation, where a document the
   * agent produced opens. One component, so a draft opened from an answer
   * and one opened from Documente cannot drift - the gap list would be what
   * quietly differed. The panel shows the form; the page adds the preview.
   */
  variant?: "page" | "panel";
}) {
  const [draft, setDraft] = useState<DraftDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState<"fields" | "gaps">("fields");
  const [openSec, setOpenSec] = useState<string | null>(null);
  // The section whose "+" menu is open, if any.
  const [picking, setPicking] = useState<string | null>(null);
  const [ring, setRing] = useState<string | null>(null);
  const [source, setSource] = useState<Citation | null>(null);
  const [renaming, setRenaming] = useState(false);
  const [title, setTitle] = useState("");
  // The second commit must win regardless of which response returns first.
  const seq = useRef(0);

  useEffect(() => {
    let live = true;
    api
      .draft(draftId)
      .then((d) => {
        if (!live) return;
        setDraft(d);
        // Open the first section with something missing; else the first.
        const secs = sectionsOf(d);
        setOpenSec((secs.find((s) => s.missing > 0) ?? secs[0])?.name ?? null);
      })
      .catch((e) => live && setError(e instanceof Error ? e.message : "Eroare"));
    return () => {
      live = false;
    };
  }, [draftId]);

  const sections = useMemo(() => (draft ? sectionsOf(draft) : []), [draft]);
  const waiting = useMemo(() => (draft ? waitingOf(draft) : []), [draft]);
  const labels = useMemo(
    () => Object.fromEntries((draft?.slots ?? []).map((s) => [s.key, s.label])),
    [draft],
  );

  async function run(work: () => Promise<DraftDetail>) {
    const mine = ++seq.current;
    setBusy(true);
    setError(null);
    try {
      const next = await work();
      if (mine === seq.current) setDraft(next);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Eroare");
    } finally {
      if (mine === seq.current) setBusy(false);
    }
  }

  // The document saves itself a beat after the lawyer stops typing. Each
  // save is the whole document, and the reply re-reads its fields, so a
  // {{placeholder}} typed in the text shows up in the form and the gap list.
  const [saving, setSaving] = useState<"" | "dirty" | "saving" | "saved" | "error">("");
  const pending = useRef<Record<string, unknown> | null>(null);
  const timer = useRef<number | undefined>(undefined);

  async function flush() {
    const doc = pending.current;
    if (!doc) return;
    pending.current = null;
    setSaving("saving");
    const mine = ++seq.current;
    try {
      const next = await api.saveContent(draftId, doc);
      if (mine === seq.current) setDraft(next);
      if (!pending.current) setSaving("saved");
    } catch (e) {
      setSaving("error");
      setError(e instanceof Error ? e.message : "Documentul nu a fost salvat.");
    }
  }

  function docChanged(doc: Record<string, unknown>) {
    pending.current = doc;
    setSaving("dirty");
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => void flush(), 1200);
  }

  // Closing the panel or leaving the page mid-sentence still saves it.
  useEffect(
    () => () => {
      window.clearTimeout(timer.current);
      if (pending.current) void api.saveContent(draftId, pending.current).catch(() => {});
    },
    [draftId],
  );

  const fill = (key: string, value: string) =>
    void run(() => api.setValues(draftId, { [key]: value }));

  // "N câmpuri lipsă →" in the panel: to the next blank chip, opened to type.
  const panelBody = useRef<HTMLDivElement>(null);
  function nextBlank() {
    const chip = panelBody.current?.querySelector<HTMLElement>(".fchip.fchip-gap [role=button]");
    chip?.scrollIntoView({ block: "center", behavior: "smooth" });
    chip?.click();
  }

  function openProvision(docId: string, path: string, note: string) {
    if (onOpenSource) onOpenSource(docId, path);
    else setSource(provisionCitation(docId, path, note));
  }

  /** A waiting field, placed: its section opens with the field ringed. */
  async function place(key: string, section: string) {
    if (!draft) return;
    await run(() => api.placeField(draft.id, key, section));
    setOpenSec(section);
    setRing(key);
    setTimeout(() => setRing((r) => (r === key ? null : r)), 2400);
  }

  /** "Completează": back to the form, the right section open, field ringed. */
  function jumpTo(key: string) {
    const slot = draft?.slots.find((s) => s.key === key);
    if (!slot) return;
    setTab("fields");
    if (slot.added && !slot.section) {
      // In no section yet: open the first "+" so it can be placed.
      setPicking(sections[0]?.name ?? null);
      return;
    }
    setOpenSec(slot.section || "Date generale");
    setRing(key);
    requestAnimationFrame(() => {
      const el = document.getElementById(`f-${key}`);
      el?.scrollIntoView({ block: "center", behavior: "smooth" });
      (el as HTMLInputElement | null)?.focus({ preventScroll: true });
    });
    setTimeout(() => setRing((r) => (r === key ? null : r)), 2400);
  }

  if (error && !draft) {
    return (
      <div className="draft-error">
        {onBack ? (
          <button className="linkbtn" onClick={onBack}>← Înapoi</button>
        ) : (
          <Link to="/documents">← Documente</Link>
        )}
        <p className="error">{error}</p>
      </div>
    );
  }
  if (!draft) return <p className="muted draft-loading">se încarcă…</p>;

  const gaps = draft.gaps;
  const panel = variant === "panel";
  const status = gaps.length > 0
    ? <span className="badge warn">{missingFields(gaps.length)}</span>
    : <span className="badge ok">complet</span>;

  const pane = (
    <>
      <div className="seg-tabs pane-tabs" role="tablist">
        <button
          role="tab"
          aria-selected={tab === "fields"}
          className={tab === "fields" ? "on" : undefined}
          onClick={() => setTab("fields")}
        >
          Câmpuri
        </button>
        <button
          role="tab"
          aria-selected={tab === "gaps"}
          className={tab === "gaps" ? "on" : undefined}
          onClick={() => setTab("gaps")}
        >
          Lipsă · {gaps.length}
        </button>
      </div>
      <div className="pane-body">
        {error && <p className="error" role="alert">{error}</p>}

        {tab === "fields" &&
          sections.map((sec) => {
            const open = openSec === sec.name;
            return (
              <div className={open ? "sec open" : "sec"} key={sec.name}>
                <div className="sec-top">
                  <button
                    className="sec-head"
                    aria-expanded={open}
                    // One section open at a time: a 21-field contract fully
                    // expanded is a wall, and the gap list is the overview.
                    onClick={() => setOpenSec(open ? null : sec.name)}
                  >
                    {sec.name}
                    {sec.slots.length > 0 && (
                      <span className={sec.missing ? "sec-st lacking" : "sec-st"}>
                        {sec.missing ? `${sec.missing} lipsă` : "complet"}
                      </span>
                    )}
                  </button>
                  <FieldPicker
                    section={sec.name}
                    waiting={waiting}
                    open={picking === sec.name}
                    onToggle={(o) => setPicking(o ? sec.name : null)}
                    onPick={(key) => void place(key, sec.name)}
                    disabled={busy}
                  />
                </div>
                {open && (
                  <div className="sec-body">
                    {sec.slots.map((slot) => (
                      <FieldInput
                        key={slot.key}
                        slot={slot}
                        value={draft.values[slot.key] ?? ""}
                        ring={ring === slot.key}
                        onCommit={(value) =>
                          void run(() => api.setValues(draft.id, { [slot.key]: value }))
                        }
                        onOpenSource={openProvision}
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          })}

        {tab === "gaps" &&
          (gaps.length === 0 ? (
            <div className="done">
              <strong>Nimic nu lipsește</strong>
              <span>Toate câmpurile cerute de lege au valoare.</span>
            </div>
          ) : (
            gaps.map((g) => (
              <div className="gapitem" key={g.key}>
                <div className="g">
                  <strong>{g.label}</strong>
                  {g.note &&
                    (g.doc_id && g.path ? (
                      <button className="art" onClick={() => openProvision(g.doc_id!, g.path!, g.note)}>
                        {g.note}
                      </button>
                    ) : (
                      <span className="art">{g.note}</span>
                    ))}
                </div>
                <button className="btn small" onClick={() => jumpTo(g.key)}>
                  {waiting.some((w) => w.key === g.key) ? "Alege categoria" : "Completează"}
                </button>
              </div>
            ))
          ))}

        {/* Deleted from the text by the lawyer, so not a field to fill - but
            the law asks for it, and that should be a choice, not a slip. */}
        {tab === "gaps" && (draft.removed ?? []).length > 0 && (
          <div className="removed">
            <p className="removed-h">Scoase din text, deși legea le cere</p>
            {draft.removed!.map((g) => (
              <div className="removed-item" key={g.key}>
                <span>{g.label}</span>
                {g.doc_id && g.path ? (
                  <button className="art" onClick={() => openProvision(g.doc_id!, g.path!, g.note)}>
                    {g.note}
                  </button>
                ) : (
                  <span className="art">{g.note}</span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );

  const editor = (
    <Suspense fallback={<p className="muted">se încarcă editorul…</p>}>
      <DocEditor
        key={draft.id}
        content={draft.content}
        values={draft.values}
        labels={labels}
        onChange={docChanged}
        onFill={fill}
      />
    </Suspense>
  );

  if (panel) {
    // Beside a conversation, the document itself, editable in place - text,
    // formatting and fields - rather than a form: what was asked for is a
    // document. A field can also be filled from the chat ("a fost încheiat
    // la Bălți"); the form and the gap list are on the Documente page.
    return (
      <aside className="src draft-panel canvas" aria-label="Document">
        <div className="cv-head">
          <div className="cv-id">
            <h2 title={draft.title}>{draft.title}</h2>
            <div className="cv-sub">
              {gaps.length > 0 ? (
                <button className="cv-gaps" onClick={() => nextBlank()} title="Mergi la următorul câmp gol">
                  {missingFields(gaps.length)} →
                </button>
              ) : (
                <span className="stat ok">complet</span>
              )}
              <span className="cv-save">{SAVE_LABEL[saving]}</span>
            </div>
          </div>
          <ExportMenu id={draft.id} />
          <Link
            to={`/documents/${draft.id}`}
            className="iconb"
            title="Deschide în Documente, cu lista câmpurilor"
            aria-label="Deschide în Documente"
          >
            <IconExpand size={18} />
          </Link>
          <button className="iconb" onClick={onBack} aria-label="Închide" title="Închide">×</button>
        </div>
        <div className="cv-body" ref={panelBody}>
          {error && <p className="error" role="alert">{error}</p>}
          {editor}
          <p className="paper-note">
            Ciornă generată automat — necesită verificarea unui avocat înainte de semnare.
          </p>
        </div>
      </aside>
    );
  }

  async function rename() {
    const next = title.trim();
    setRenaming(false);
    if (!next || next === draft!.title) return;
    await run(() => api.renameDraft(draft!.id, next));
  }

  return (
    <div className="draft">
      <div className="draft-bar">
        <Link to="/documents" className="back">
          <IconBack size={18} />
          Documente
        </Link>
        {renaming ? (
          <input
            className="title-input"
            value={title}
            autoFocus
            aria-label="Denumirea documentului"
            onChange={(e) => setTitle(e.target.value)}
            onBlur={() => void rename()}
            onKeyDown={(e) => {
              if (e.key === "Enter") void rename();
              if (e.key === "Escape") setRenaming(false);
            }}
          />
        ) : (
          <button
            className="title"
            title="Redenumește"
            onClick={() => {
              setTitle(draft.title);
              setRenaming(true);
            }}
          >
            {draft.title}
          </button>
        )}
        <span className="fineprint">{busy ? "se salvează…" : SAVE_LABEL[saving]}</span>
        {status}
        <ExportMenu id={draft.id} large />
      </div>

      <div className="work">
        <section className="pane" aria-label="Completare">{pane}</section>

        <section className="paper-wrap" aria-label="Document">
          <p className="paper-note">
            Document generat automat. Necesită verificarea unui avocat înainte
            de semnare — nu constituie consultanță juridică.
          </p>
          {editor}
        </section>

        {source && (
          <DocumentPanel
            key={source.chunk_id}
            citation={source}
            onClose={() => setSource(null)}
          />
        )}
      </div>
    </div>
  );
}
